import React from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import { fetchReg } from "@/redux/profile/authAndReg/asyncActions";

import {
  NameInput,
  EmailInput,
  PhoneInput,
  PasswordInput,
  Button,
} from "@/components";

import styles from "./RegisterForm.module.scss";

import { ValidMessages } from "@/resources/locale";
import { RegValuesType } from "./types";
import { addEmail, addName, addPassword, addPhone } from "@/redux/profile/settings/changePass/slice";
import { cleanPhone } from "@/utils/form/phoneMask";

const SMS_COOLDOWN_KEY = 'sms_last_sent';
const SMS_COOLDOWN_MS = 60000;

function getSmsCooldownRemaining(): number {
  const lastSent = sessionStorage.getItem(SMS_COOLDOWN_KEY);
  if (!lastSent) return 0;
  const elapsed = Date.now() - Number(lastSent);
  return elapsed < SMS_COOLDOWN_MS ? Math.ceil((SMS_COOLDOWN_MS - elapsed) / 1000) : 0;
}

type RegisterFormProps = {
  handleSubmitForm?: () => void
}
export const RegisterForm: React.FC<RegisterFormProps> = (props) => {
  const { handleSubmitForm } = props;
  const { isLoading, errorMessage } = useAppSelector(state => state.authAndReg);
  const dispatch = useAppDispatch();
  const [cooldown, setCooldown] = React.useState(getSmsCooldownRemaining());

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      const remaining = getSmsCooldownRemaining();
      setCooldown(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const initialValues: RegValuesType = {
    name: "",
    email: "",
    phone: "",
    password: "",
    passwordRepeat: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(ValidMessages.inputs.name.REQUIRED),
    email: Yup.string()
      .email(ValidMessages.inputs.email.INCORRECT)
      .required(ValidMessages.inputs.email.REQUIRED),
    phone: Yup.string()
      .min(18, ValidMessages.inputs.phone.NOT_FULL)
      .required(ValidMessages.inputs.phone.REQUIRED),
    password: Yup.string()
      .required(ValidMessages.inputs.password.REQUIRED)
      .min(6, ValidMessages.inputs.password.NOT_FULL),
    passwordRepeat: Yup.string()
      .required(ValidMessages.inputs.passwordRepeat.REQUIRED)
      .oneOf([Yup.ref("password"), null], ValidMessages.inputs.passwordRepeat.NOT_MATCH),
  });

  const handleSubmit = async (
    values: RegValuesType,
  ) => {
    if (getSmsCooldownRemaining() > 0) return;

    try {
      const phone = cleanPhone(values.phone);

      const data = {
        name: values.name,
        email: values.email,
        phone: phone,
        password: values.password,
        partner_id: '1',
      };

      await dispatch(fetchReg(data)).unwrap();

      sessionStorage.setItem(SMS_COOLDOWN_KEY, String(Date.now()));
      setCooldown(60);

      dispatch(addPhone(phone));
      dispatch(addName(values.name));
      dispatch(addEmail(values.email));
      dispatch(addPassword({
        password: values.password,
        passwordRepeat: values.passwordRepeat,
      }));

      handleSubmitForm && handleSubmitForm()
    } catch (error: any) {
      console.log(error);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(values)}
      validateOnChange={false}
      validateOnBlur={true}
    >
      {(formik) => {
        const isNameFilled = formik.values.name.trim().length > 0;
        const isEmailFilled = formik.values.email.trim().length > 0;
        const isPhoneFilled = formik.values.phone.trim().length >= 18;
        const isPasswordFilled = formik.values.password.trim().length >= 6;
        const isPasswordRepeatFilled = formik.values.passwordRepeat.trim().length >= 6;
        const isFormValid = isNameFilled && isEmailFilled && isPhoneFilled && isPasswordFilled && isPasswordRepeatFilled;

        return (
          <Form className={styles.form}>
            <div className={styles.formSection}>
              <NameInput
                name="name"
                lable="ФИО"
                placeholder="Иванов Иван"
              />
              <ErrorMessage component="div" className={styles.errorMessage} name="name" />
            </div>
            <div className={styles.formSection}>
              <EmailInput
                name="email"
                lable="Email (для чеков при оплате)"
                placeholder="example@example.com"
              />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="email"
              />
            </div>
            <div className={styles.formSection}>
              <PhoneInput
                name="phone"
                lable="Телефон"
                placeholder="+7 (___) ___-__-__"
              />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="phone"
              />
            </div>
            <div className={styles.formSection}>
              <PasswordInput
                name="password"
                placeholder="Пароль"
                lable="Пароль"
              />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="password"
              />
            </div>
            <div className={styles.formSection}>
              <PasswordInput
                name="passwordRepeat"
                placeholder="Повторите пароль"
                lable="Повторите пароль"
              />
              <ErrorMessage
                component="div"
                className={styles.errorMessage}
                name="passwordRepeat"
              />
            </div>

            {errorMessage && (
              <div className={styles.errorMessage}>
                {errorMessage}
              </div>
            )}

            <Button
              type="submit"
              newStyles={styles.button}
              disabled={
                formik.isSubmitting || isLoading || !isFormValid || cooldown > 0
              }
            >
              {cooldown > 0 ? `Повторить через ${cooldown} сек` : "Продолжить"}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};
